import { useMemo } from "react";
import {
  Card,
  Switch,
  TimePicker,
  Text,
  Divider,
  TextField,
  IconButton,
} from "@techzyapp/ui-lib";
import { WorkingHoursEnum } from "utils/enums";
import dayjs from "dayjs";
import { useFormikContext, useField } from "formik";
import styles from "./styles.module.css";

export interface WorkingHoursProps {
  day: number;
  startTime: string | null;
  endTime: string | null;
}

interface WorkingHoursDataProps {
  name: string;
}

const allDays: WorkingHoursProps[] = [
  { day: 1, startTime: null, endTime: null },
  { day: 2, startTime: null, endTime: null },
  { day: 3, startTime: null, endTime: null },
  { day: 4, startTime: null, endTime: null },
  { day: 5, startTime: null, endTime: null },
  { day: 6, startTime: null, endTime: null },
  { day: 0, startTime: null, endTime: null },
];

const DEFAULT_START = "09:00:00";
const DEFAULT_END = "18:00:00";

const WorkingHours = ({ name }: WorkingHoursDataProps) => {
  const { setFieldValue } = useFormikContext<any>();
  const [field] = useField<WorkingHoursProps[]>({ name });

  const mergedWorkingHours = useMemo(() => {
    const current = field.value || [];
    return allDays.map((defaultDay) => {
      const found = current.find((d) => d.day === defaultDay.day);
      return {
        ...defaultDay,
        ...found,
      };
    });
  }, [field.value]);

  const updateDay = (
    day: number,
    updatedValues: Partial<WorkingHoursProps>
  ) => {
    const updated = mergedWorkingHours.map((item) =>
      item.day === day ? { ...item, ...updatedValues } : item
    );
    setFieldValue(name, updated);
  };

  const handleTimeChange = (
    day: number,
    type: "startTime" | "endTime",
    value: dayjs.Dayjs | null
  ) => {
    updateDay(day, {
      [type]: value ? value.toISOString() : null,
    });
  };

  const handleSwitchChange = (day: number, isEnabled: boolean) => {
    updateDay(day, {
      startTime: isEnabled
        ? dayjs(DEFAULT_START, "HH:mm:ss").toISOString()
        : null,
      endTime: isEnabled ? dayjs(DEFAULT_END, "HH:mm:ss").toISOString() : null,
    });
  };

  return (
    <div className={styles.workingHours}>
      <Card
        title="Working Hours"
        headerActions={
          <IconButton iconName="Clock" variant="filled" nonClickable />
        }
      >
        <div className={styles.items}>
          {mergedWorkingHours.map((item) => {
            const enabled = !!item.startTime;
            return (
              <div key={item.day}>
                <div className={styles.item}>
                  <div className={styles.switch}>
                    <Switch
                      label={WorkingHoursEnum[item.day]}
                      checked={enabled}
                      onChange={(e) =>
                        handleSwitchChange(item.day, e.target.checked)
                      }
                    />
                  </div>
                  <div className={styles.inputs}>
                    {enabled ? (
                      <>
                        <TimePicker
                          value={item.startTime ? dayjs(item.startTime) : null}
                          onChange={(value) =>
                            handleTimeChange(item.day, "startTime", value)
                          }
                        />
                        <span className={styles.space}>
                          <Text>To</Text>
                        </span>
                        <TimePicker
                          value={item.endTime ? dayjs(item.endTime) : null}
                          onChange={(value) =>
                            handleTimeChange(item.day, "endTime", value)
                          }
                          disabled={!item.startTime}
                          // minTime={
                          //   item.startTime ? dayjs(item.startTime) : undefined
                          // }
                        />
                      </>
                    ) : (
                      <TextField
                        placeholder="Closed"
                        adornmentPosition="start"
                        fullWidth
                        iconName="Moon"
                        disabled
                      />
                    )}
                  </div>
                </div>
                <Divider />
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
};

export default WorkingHours;
